import { useContext, useEffect, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';

import { User } from '@esg/esg-global-types';

import type { GetUsersPageFilters } from '../../../graphql/services/user.service';
import { CompanyContext } from '../../../context/CompanyContext';
import { GroupContext } from '../../../context/GroupContext';
import { BodyText, CaptionText } from '../../shared/typography/PrimaryTypography';

const ASSIGNEE_PAGE_SIZE = 200;
const ASSIGNEE_MAX_PAGES = 5;

/** A reviewer that a record can be assigned to. */
export interface AssigneeOption {
  /** The user's id — the same SSO uid honey stores on the record. */
  id: string;
  /** Display name, or the email when the user has no name on file. */
  name: string;
  /** Contact email, shown to disambiguate people with the same name. */
  email: string;
}

/**
 * Maps a platform user to a pickable assignee, or null when the document carries
 * nothing a human could recognise (no name and no email).
 * @param {User} user The platform user document.
 * @returns {AssigneeOption | null} The option, or null when unidentifiable.
 */
export function toAssigneeOption(user: User): AssigneeOption | null {
  const email = (user.email ?? '').trim();
  const name = [user.first_name, user.last_name]
    .map((part) => (part ?? '').trim())
    .filter(Boolean)
    .join(' ');
  if (name === '' && email === '') {
    return null;
  }
  return { id: user.id, name: name === '' ? email : name, email };
}

/**
 * True when a user may work on the company (or group) the reviewer is logged in
 * under, and so is a sensible person to hand a record to.
 *
 * A super admin passes everywhere; otherwise the user's document must grant them
 * the company explicitly, or admin over its group. With no login context — an EP
 * user browsing outside a company — nobody is excluded, because there is no
 * company to judge access against.
 *
 * This narrows the PICKER, not the permission: honey stores whatever uid it is
 * given and has no per-company model yet (its role store deliberately collapses to
 * one global level until tenancy lands). Maxine asked whether the list was already
 * scoped — it was not (2026-08-14 ⑥) — and an unscoped list invites handing a
 * record to someone who cannot open it.
 *
 * @param {User} user The platform user document.
 * @param {string | null} group_id The logged-in group id, when there is one.
 * @param {string | null} company_id The logged-in company id, when there is one.
 * @returns {boolean} Whether the user should be offered as an assignee.
 */
export function hasAccessToCompany(
  user: User,
  group_id: string | null,
  company_id: string | null
): boolean {
  if (user.super_admin === true) {
    return true;
  }
  if (group_id === null && company_id === null) {
    return true;
  }
  const groups = user.groups ?? {};
  if (group_id === null) {
    // Company known but not its group: accept the grant wherever it appears.
    return Object.values(groups).some(
      (group) => company_id !== null && group?.companies?.[company_id] !== undefined
    );
  }
  const group = groups[group_id];
  if (group === undefined) {
    return false;
  }
  // A group admin covers every company beneath it, including ones added later.
  if (group.admin === true) {
    return true;
  }
  return company_id === null || group.companies?.[company_id] !== undefined;
}

interface AssigneeSelectProps {
  /** Currently assigned SSO uid, or null when unassigned. */
  value: string | null;
  /** Resolved display name of the current assignee, when the API supplied one. */
  valueName?: string | null;
  /** Called with the chosen uid, or null when the selection is cleared. */
  onChange: (assignedToId: string | null) => void;
  /** Disable the control while a marker update is in flight. */
  disabled?: boolean;
}

/**
 * Reviewer picker for record assignment, searchable by name or email.
 *
 * Replaces a free-text "Assignee (SSO uid)" field: reviewers do not know each
 * other's uids, and the field displayed one back at them (Maxine 2026-08-03 ⑧).
 * The uid is still what gets STORED — only the human-facing surface changes.
 *
 * The directory comes from the platform's own user store, so it is the same set of
 * people the rest of the app knows about. A failed load leaves an empty list rather
 * than blocking the rest of the markers panel; the current assignee still renders
 * because it falls back to the name/uid the record already carries.
 * @param {AssigneeSelectProps} props The current assignment and change handler.
 * @returns {JSX.Element}
 */
export default function AssigneeSelect({
  value,
  valueName = null,
  onChange,
  disabled
}: AssigneeSelectProps) {
  const [options, setOptions] = useState<Array<AssigneeOption>>([]);
  const [directoryUnavailable, setDirectoryUnavailable] = useState<boolean>(false);
  const { company } = useContext(CompanyContext);
  const { group } = useContext(GroupContext);
  const company_id = company?.id ?? null;
  const group_id = group?.id ?? null;

  useEffect(() => {
    let cancelled = false;
    setDirectoryUnavailable(false);
    // Imported lazily, and deliberately: the user-directory module builds an Apollo
    // client at module scope, which needs a `fetch` the module graph cannot assume.
    // A static import made merely RENDERING an ancestor of this component pull Apollo
    // in, which broke unrelated component tests at load time.
    import('../../../graphql/services/user.service')
      .then(async ({ getUsersPage }) => {
        const filter: GetUsersPageFilters =
          group_id !== null
            ? { group_id, include_deleted: false }
            : company_id !== null
              ? { company_id, include_deleted: false }
              : { include_deleted: false };
        const users: Array<User> = [];
        let cursor: string | null = null;
        let pageCount = 0;
        do {
          const page = await getUsersPage(filter, cursor, ASSIGNEE_PAGE_SIZE);
          users.push(...page.users);
          cursor = page.nextCursor;
          pageCount += 1;
        } while (cursor !== null && pageCount < ASSIGNEE_MAX_PAGES && !cancelled);
        return users;
      })
      .then((users) => {
        if (!cancelled) {
          setOptions(
            users
              .filter((user) => hasAccessToCompany(user, group_id, company_id))
              .map(toAssigneeOption)
              .filter((option): option is AssigneeOption => option !== null)
              .sort((left, right) => left.name.localeCompare(right.name))
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDirectoryUnavailable(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [group_id, company_id]);

  // The assigned user may not be in the loaded directory (deleted, or a slow load),
  // so fall back to what the record itself knows rather than showing an empty field.
  const selected =
    value === null
      ? null
      : (options.find((option) => option.id === value) ?? {
          id: value,
          name: valueName ?? value,
          email: ''
        });

  return (
    <Autocomplete
      size="small"
      fullWidth
      options={options}
      value={selected}
      disabled={disabled}
      onChange={(_event, next) => onChange(next === null ? null : next.id)}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, current) => option.id === current.id}
      renderOption={(props, option) => (
        // Email under the name: two colleagues can share a first and last name.
        <li {...props} key={option.id}>
          <span>
            <BodyText noWrap>{option.name}</BodyText>
            {option.email !== '' && <CaptionText noWrap>{option.email}</CaptionText>}
          </span>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Assignee"
          placeholder="Search by name or email"
          helperText={
            directoryUnavailable ? 'Directory unavailable — try again shortly' : undefined
          }
        />
      )}
    />
  );
}
